// "Estimation" (owner 2026-10-20): the desktop app's estimation side - project list, timeline,
// pre-estimation, the estimate editor, export and re-estimate - built from Rust to WebAssembly
// (repo bidlight-estimation) and installed under /estimation-app/ next to this bundle. It runs
// same-origin, so it reads the signed-in token from localStorage itself; back/app.js serves its
// index.html for any deep link under /estimation-app/.
//
// Height: the SaaS renders under html { zoom: var(--app-zoom) } (0.8, App.scss), and viewport
// units do not follow that zoom, so the old `calc(100vh - 64px)` painted 20% short and left a
// blank band under the app (owner 2026-10-25, "not filling the whole page"). The frame is sized
// from where it actually starts on screen to the bottom of the window, in layout pixels, and is
// re-measured on resize and when anything above it (the header) changes height.
import React, { useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// The root zoom as a number (1 when the browser reports none).
export function rootZoom() {
  const z = parseFloat(getComputedStyle(document.documentElement).zoom);
  return Number.isFinite(z) && z > 0 ? z : 1;
}

// Layout-pixel height that takes an element whose top is at `topPx` (screen pixels, as
// getBoundingClientRect reports under a zoomed root) down to the bottom of the window.
export function fillHeight(windowHeight, topPx, zoom) {
  return Math.max(200, Math.floor((windowHeight - Math.max(0, topPx)) / (zoom || 1)));
}

export default function EstimationPage() {
  // /estimation/<rest> opens the app at its own route <rest>
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\/estimation\/?/, "");
  const ref = useRef(null);
  const [height, setHeight] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const h = fillHeight(window.innerHeight, el.getBoundingClientRect().top, rootZoom());
      setHeight((prev) => (prev === h ? prev : h));
    };
    measure();
    window.addEventListener("resize", measure);
    // the header can wrap or a banner can appear above the frame
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) {
      ro.observe(document.body);
      const header = document.querySelector("header, .navbar, nav");
      if (header) ro.observe(header);
    }
    return () => {
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  // The page itself never scrolls: the app inside the frame owns its scrolling.
  useLayoutEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <iframe
      ref={ref}
      title="Estimation"
      data-testid="estimation-app"
      src={`/estimation-app/${rest}${search}`}
      allow="fullscreen"
      style={{
        display: "block",
        width: "100%",
        height: height ? `${height}px` : "calc(100 * var(--vh) - 64px)",
        border: 0,
      }}
    />
  );
}
